import { useEffect } from 'react';
import Layout from '../../../../../components/layout/Layout';
import Breadcrumb from '../../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../../utils/seo';

// ===== Schedule Management Training (EN) redesign (Implementer C scope) =====
// Design base: home / about/overview new style. Canonical EN copy only (creation forbidden):
//   Hero/overview/features = this page's prior working-tree copy. Pricing / What's Included / application flow =
//   verbatim canonical boilerplate shared with the completed EN detail pages. Factual data unchanged.
//   Page-specific EN curriculum/outcomes prose did not exist in the working tree and is NOT fabricated here.

function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
const TEXT_ARROW = "inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.06em] border-b border-[#a38e65] pb-1 hover:text-[#8f7a56] transition-colors duration-300";

const OVERVIEW = [
  "A butler's day operates on a minute-by-minute schedule. From morning preparations, meal service, guest reception, estate management, event preparation, to emergency response—they advance multiple tasks in parallel while completing everything perfectly. Behind this lies scientific and practical time management methods.",
  'This training teaches butler schedule management techniques from both theoretical and practical perspectives. You will learn priority setting using the Eisenhower Matrix, time blocking methods, securing buffer time, responding to unexpected situations, effective use of digital calendars and task management tools, and weekly/monthly review practices—all immediately applicable to practical work. Through individual work and group discussions, you will build a time management system optimized for yourself.',
  'This training is ideal for hotel and inn staff, restaurant managers, concierges, sales professionals, and all occupations that handle diverse tasks simultaneously.',
];

const FEATURES = [
  { icon: 'ri-lightbulb-line', t: 'Scientific Methods', d: 'Proven time management theory.' },
  { icon: 'ri-tools-line', t: 'Practical Tools', d: 'Ready-to-use templates provided.' },
  { icon: 'ri-user-settings-line', t: 'Personal Optimization', d: 'Build your own system.' },
];

const SPECS = [
  { k: 'Training Duration', v: '3 hours per day (morning/afternoon available)' },
  { k: 'Participants', v: '10-20 people' },
  { k: 'Target Level', v: 'All Levels' },
  { k: 'Location', v: "Your company's meeting room or our association venue" },
  { k: 'Customization', v: 'Content adjustment available (separate consultation)' },
];

const INCLUDES = ['Training program implementation', 'Textbook materials (for all participants)', 'Certificates (for all participants)', 'Instructor transportation (within Tokyo)', 'Post-training follow-up (1 session)'];

const FLOW = [
  { t: 'Inquiry & Consultation', d: "Please feel free to contact us. We will listen to your company's challenges and goals." },
  { t: 'Consultation & Customization', d: 'We will learn about your industry, participant attributes, field challenges, and training objectives to design the optimal program.' },
  { t: 'Schedule Coordination & Contract', d: 'We will finalize the implementation date, location, and pricing, then proceed with the contract.' },
  { t: 'Training Implementation', d: 'Experienced instructors will conduct practical training.' },
  { t: 'Follow-up', d: 'We will continue to follow up after training and support field implementation.' },
];

const RELATED = [
  { label: 'Team Leadership Training', href: '/en/seminars/corporate/team-leadership', desc: 'Leadership Techniques from Butlers' },
  { label: 'Crisis Management Training', href: '/en/seminars/corporate/crisis-management', desc: 'Safety and Prevention from Butlers' },
  { label: 'Corporate Training Programs List', href: '/en/seminars', desc: 'View all training programs' },
];

export default function EnScheduleManagementPage() {
  useEffect(() => {
    updateSEO({
      title: 'Schedule Management Training | Time Management from Butlers | Japan Butler Association',
      description: 'Japan Butler Association corporate schedule management training. Learn efficient time management, priority setting, and multitasking techniques practiced by butlers. 3 hours/day, 10-20 participants. Fee: ¥400,000.',
      keywords: 'schedule management training,time management,productivity,butler,corporate training,Japan Butler Association',
      canonical: 'https://butler-association.jp/en/seminars/corporate/schedule-management',
      hreflang: { ja: 'https://butler-association.jp/seminars/corporate/schedule-management', en: 'https://butler-association.jp/en/seminars/corporate/schedule-management' },
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Seminars', href: '/en/seminars' },
    { label: 'Schedule Management Training' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-corp-schedule-management.webp" alt="Schedule Management Training with a butler guiding participants" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[760px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Schedule Management</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">Schedule Management Training</h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              Schedule Management Training is a practical program that applies the precise time management techniques practiced daily by butlers to modern business professionals. Butlers manage the schedules of multiple employers and family members while simultaneously advancing estate operations, guest reception, and event preparations. Time management techniques for achieving maximum results with limited time can be directly applied to today's busy business environment. This training systematically teaches skills directly applicable to practical work, from basic time management theory, scientific priority setting methods, to effective use of digital tools.
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
          <p className="border-l-2 border-[#a38e65] bg-[#f8f6f0] px-5 py-4 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
            ⚠️ This training aims at skill acquisition and does not guarantee improved performance or increased productivity.
          </p>
        </div>
      </div>

      <NavyBand no="I" ja="About This Training" en="Overview" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start mb-12 sm:mb-14">
            <div>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-6">
                Designing time, learned from <span className="text-[#a38e65]">a minute-by-minute floor</span>
              </h3>
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                {OVERVIEW.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <img src="/images/generated/jba-p-038-sched-plan.webp" alt="Planning time allocation during the schedule management training" className="w-full h-64 lg:h-[380px] object-cover border border-[#a38e65]/30" />
          </div>
          <div className="grid sm:grid-cols-3 border-t-2 border-[#1a2942]">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex flex-col gap-4 border-b border-[#e2ddd2] sm:border-b-0 sm:border-r last:border-r-0 py-7 sm:px-6 first:sm:pl-0">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-[#a38e65]/50 text-[#a38e65]"><i className={`${f.icon} text-2xl`}></i></span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">{f.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New variation 1 (large fee + hairline spec table + includes grid). Origin: home IV Fee + home VII table. */}
      <NavyBand no="II" ja="Format & Pricing" en="Details & Fee" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[360px_1fr] gap-10 lg:gap-14 items-start">
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 text-center">
              <div className="font-['Noto_Serif_JP',serif] text-sm text-[#d7b779] tracking-[0.1em] mb-3">Fee (tax included)</div>
              <div className="font-['Playfair_Display',serif] text-[#f8f6f0] text-5xl sm:text-6xl leading-none">¥400,000</div>
              <div className="w-11 h-px bg-[#a38e65] mx-auto mt-6 mb-5"></div>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">3 hours per day for 10-20 participants.</p>
            </div>
            <div>
              <dl className="border-t-2 border-[#1a2942]">
                {SPECS.map((s) => (
                  <div key={s.k} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[180px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{s.k}</dt>
                    <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{s.v}</dd>
                  </div>
                ))}
              </dl>
              <h4 className="jba-display font-medium text-[#1a2942] text-lg mt-10 mb-5">What's Included</h4>
              <ul className="grid sm:grid-cols-2 gap-x-8">
                {INCLUDES.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><i className="ri-check-line text-[#a38e65]"></i><span>{inc}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* New variation 2 (numbered vertical stepper). Origin: about/overview timeline adapted to a process. */}
      <NavyBand no="III" ja="Application Process" en="How to Apply" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <ol className="relative border-l border-[#e2ddd2] ml-5 max-w-4xl">
            {FLOW.map((s, i) => (
              <li key={s.t} className="relative pl-8 sm:pl-10 pb-9 last:pb-0">
                <span className="absolute -left-[19px] top-0 w-9 h-9 rounded-full bg-[#1a2942] text-[#d7b779] flex items-center justify-center font-['Playfair_Display',serif] text-sm border border-[#a38e65]/50">{i + 1}</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-1.5">{s.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
              </li>
            ))}
          </ol>
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 mt-12 text-center">
            <h4 className="jba-display font-medium text-xl sm:text-2xl text-[#f8f6f0] mb-3">Application &amp; Inquiries</h4>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] mb-7 max-w-2xl mx-auto leading-[1.9]">For questions or applications regarding Schedule Management Training, please feel free to contact us.</p>
            <a href="/en/contact" className={BTN08_ON_NAVY}>Apply for Schedule Management Training</a>
          </div>
        </div>
      </section>

      <NavyBand no="IV" ja="Related Training Programs" en="Related Programs" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-8">
            {RELATED.map((r) => (
              <a key={r.href} href={r.href} className="group bg-white border border-[#e2ddd2] p-8 flex flex-col hover:border-[#a38e65]/70 transition-colors duration-300">
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 group-hover:text-[#8f7a56] transition-colors">{r.label}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9] mb-6">{r.desc}</p>
                <span className={`${TEXT_ARROW} mt-auto self-start`}>View Details <span className="text-[#a38e65]">→</span></span>
              </a>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="/en/seminars" className={BTN08_ON_LIGHT}>Back to Seminars &amp; Events</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
